import React, { ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/24/solid';
import { AuthMethodType } from 'models/Auth';
import { ProviderLogo } from './SignInBtn';

const PROVIDER_NAMES: Partial<Record<AuthMethodType, string>> = {
  [AuthMethodType.Google]: 'Google',
  [AuthMethodType.Apple]: 'Apple',
  [AuthMethodType.Facebook]: 'Facebook',
};

type Props = {
  onAction: () => void;
} & (
  | { state: 'authenticating'; authMethod?: AuthMethodType }
  | { state: 'authenticated' }
);

// Shown in place of the sign-in options while the provider pop-up is open,
// and once the user is signed in.
const AuthStatus = (props: Props): ReactElement => {
  const t = useTranslations();
  const isAuthenticating = props.state === 'authenticating';
  const authMethod = isAuthenticating ? props.authMethod : undefined;
  const provider = authMethod && PROVIDER_NAMES[authMethod];

  return (
    <div className={styles.container} role="status" aria-live="polite">
      {isAuthenticating ? (
        <div className={styles.badge}>
          <span className={styles.ring} aria-hidden="true" />
          <span className={styles.spinner} aria-hidden="true" />
          {provider && <ProviderLogo authMethod={authMethod} />}
        </div>
      ) : (
        <div className={classNames(styles.badge, styles.success)}>
          <CheckIcon className="h-7 w-7" aria-hidden="true" />
        </div>
      )}

      <div className={styles.text}>
        <p className={styles.title}>
          {isAuthenticating
            ? provider
              ? t('auth.status.waitingFor', { provider })
              : t('auth.status.signingIn')
            : t('auth.status.signedIn')}
        </p>
        {isAuthenticating && (
          <p className={styles.subtitle}>{t('auth.status.popupHint')}</p>
        )}
      </div>

      <button type="button" onClick={props.onAction} className={styles.action}>
        {isAuthenticating ? t('common.cancel') : t('auth.logOut')}
      </button>
    </div>
  );
};

export default AuthStatus;

const styles = {
  container: 'flex w-72 flex-col items-center gap-5 py-2',
  badge: classNames(
    'relative flex h-16 w-16 items-center justify-center rounded-full',
    'bg-white shadow-[0_8px_24px_-8px_rgba(39,87,186,0.35)]',
  ),
  // Faint full circle behind the spinning arc.
  ring: 'absolute -inset-1.5 rounded-full border-2 border-mf-blue/15',
  spinner: classNames(
    'absolute -inset-1.5 animate-spin rounded-full border-2 border-transparent',
    'border-t-mf-blue',
  ),
  success: 'bg-mf-green-400 text-white',
  text: 'space-y-1.5 text-center',
  title: 'text-lg font-semibold text-mf-off-black-950',
  subtitle: 'text-sm text-balance text-mf-off-black-500',
  action: classNames(
    'rounded-full px-4 py-1.5 text-sm font-medium text-mf-gray-500',
    'transition-colors duration-150 hover:bg-mf-blue/5 hover:text-mf-off-black-950',
  ),
};
